import React, { memo, FC, useEffect, useState, useRef } from "react";
import { MobileOnly, TabletAndLarger } from "@montana/shared/responsive";
import { Button, ButtonStyles } from "@montana/shared/button";
import { useTranslations } from "@montana/containers/translations";
import { StoreListItem } from "./store-list-item.component";
import { IEnhancedStore } from "@montana/models/dev";
import { Spinner } from "@montana/shared/spinner/spinner.component";
import { areEqual, VariableSizeList } from 'react-window';
import { AutoSizer } from "react-virtualized";

const MOBILE_SHOW_COUNT = 4;

export interface IStoreListProps {
    loadingStores: boolean;
    visibleStores: IEnhancedStore[];
    onStoreSelect: (store: IEnhancedStore) => void;
    selectedStore?: IEnhancedStore;
}

const StoreList: FC<IStoreListProps> = memo(({ loadingStores, onStoreSelect, selectedStore, visibleStores }: IStoreListProps) => {
    const [visibleCount, setVisibleCount] = useState(MOBILE_SHOW_COUNT);
    const [storesWithOpenTimeTable, setStoresWithOpenTimeTable] = useState<string[]>([])
    const listRef = useRef<VariableSizeList>(null);
    const translate = useTranslations();

    useEffect(() => {
        const index = visibleStores.findIndex(v => v.id === selectedStore?.id)
        listRef.current?.scrollToItem(index, 'start');
    }, [selectedStore]);

    useEffect(() => {
        setVisibleCount(MOBILE_SHOW_COUNT);
    }, [visibleStores]);

    function onShow4More() {
        setVisibleCount(prev => prev + MOBILE_SHOW_COUNT);
    }

    // As we use virtual scrolling here we need to calculate the height. Thus if the design is altered, so must this.
    function getRowHeight(store: IEnhancedStore): number {
        let size = 505; // The height of a single store item when the timetable is expanded and everything else is displayed.

        if (store.name && store.name.length > 37) {
            size += 32; // If we got a long name we expect it to wrap and need to take it into account.
        }
        if (!store.country) {
            size -= 20;
        }
        if (!store.email) {
            size -= 24;
        }
        if (!store.phoneNumber) {
            size -= 24;
        }
        if (!store.shopTimeTable?.length) {
            size -= 202;
        } else if (!storesWithOpenTimeTable.find(id => id === store.id)) {
            size -= 154;
        }

        return size;
    }

    const handleTimeTableToggle = (store: IEnhancedStore, index: number, isOpen: boolean) => {
        setStoresWithOpenTimeTable(prev => {
            const isOpened = prev.findIndex(i => i === store.id);
            if (!isOpen && isOpened > -1) {
                prev.splice(isOpened, 1);
            }

            if (isOpen && isOpened < 0) {
                prev.push(store.id || '');
            }

            return prev;
        });

        listRef.current?.resetAfterIndex(index);
    }

    return (
        <div className={`store-list${!visibleStores?.length ? ' store-list--no-result' : ''}`} id="store-list">
            {!loadingStores && visibleStores.length ? (
                <>
                    <TabletAndLarger>
                        <div style={{
                            height: "700px",
                            width: "500px",
                        }}>
                            <AutoSizer>
                                {({ height, width }) => (
                                    <VariableSizeList
                                        className="store-list__virtual-list"
                                        ref={listRef}
                                        height={height}
                                        width={width}
                                        itemSize={(index: number) => getRowHeight(visibleStores[index])}
                                        itemCount={visibleStores.length}
                                        itemData={visibleStores}
                                    >
                                        {memo(({ data, index, style }) => {
                                            const store = data[index];

                                            return (
                                                <StoreListItem
                                                    style={style}
                                                    key={`store-${store.id}`}
                                                    show
                                                    withHorizontalPadding
                                                    store={store}
                                                    onTimeTableToggle={(isOpen: boolean) => handleTimeTableToggle(store, index, isOpen)}
                                                    selected={selectedStore?.id === store.id}
                                                    onSelect={() => onStoreSelect(store)}
                                                />
                                            )
                                        }, areEqual)}
                                    </VariableSizeList>
                                )}
                            </AutoSizer>
                        </div>
                    </TabletAndLarger>
                    <MobileOnly includeSSR>
                        {visibleStores.map((store, index) => {
                            const showStore = visibleStores.slice(0, visibleCount).includes(store);

                            return (
                                <StoreListItem
                                    key={`store-mobile-${store.id}-${index}`}
                                    store={store}
                                    show={showStore}
                                    selected={selectedStore?.id === store.id}
                                    onSelect={() => onStoreSelect(store)}
                                />
                            )
                        })}
                        {!!visibleCount && visibleStores && visibleStores.length > 0 && visibleCount < visibleStores.length &&
                            <Button
                                className="store-list__show-more"
                                style={ButtonStyles.Secondary}
                                text={translate(t => t.storeLocator.show4More, '[Show 4 more]')}
                                onClick={onShow4More}
                            />
                        }
                    </MobileOnly>
                </>
            ) : (
                <div className="store-list__no-result-text">
                    {!loadingStores && translate(t => t.storeLocator.noStoreResults, `[We couldn't find any stores.]`)}
                    {loadingStores && <Spinner />}
                </div>
            )}
        </div>
    )
});

export default StoreList;
